import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  KeyRound,
  Mail,
  ShieldCheck,
  ArrowLeft,
} from "lucide-react";
import { Investor } from "@/lib/api";
import { useRecaptchaToken, RECAPTCHA_ENABLED } from "@/lib/recaptcha";

/**
 * Two-step password reset UI for the Investor Portal.
 *
 * 1) /investor/forgot-password  — investor enters email, we email a reset link
 * 2) /investor/reset-password?token=... — investor picks a new password
 *                                          (also handles first-time activation)
 */

const Shell = ({ testId, eyebrow, title, children }) => (
  <div
    data-testid={testId}
    className="min-h-screen bg-[#04102B] text-white flex items-center justify-center px-5 py-10"
  >
    <div className="w-full max-w-md">
      <Link to="/" className="block text-center mb-8">
        <div className="font-display text-3xl tracking-tight">SAHYOG FINANCE</div>
        <div className="text-[#D4AF37] text-xs tracking-[0.22em] uppercase mt-1">
          {eyebrow}
        </div>
      </Link>
      <div className="bg-[#0A1F44] rounded-3xl p-7 border border-[#D4AF37]/30">
        <h1 className="font-display text-2xl">{title}</h1>
        {children}
      </div>
      <div className="mt-5 text-center">
        <Link
          to="/investor/login"
          className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-[#F0D788]"
        >
          <ArrowLeft size={12} /> Back to sign in
        </Link>
      </div>
    </div>
  </div>
);

const RecaptchaNotice = () =>
  RECAPTCHA_ENABLED ? (
    <p className="mt-4 text-[11px] text-white/40 flex items-start gap-1.5 leading-snug">
      <ShieldCheck size={11} className="mt-0.5 text-[#D4AF37] shrink-0" />
      <span>
        Protected by reCAPTCHA — the Google{" "}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-[#F0D788]"
        >
          Privacy Policy
        </a>{" "}
        and{" "}
        <a
          href="https://policies.google.com/terms"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-[#F0D788]"
        >
          Terms of Service
        </a>{" "}
        apply.
      </span>
    </p>
  ) : null;

/* ─── STEP 1 · Forgot password ────────────────────────────────────── */
export function InvestorForgotPassword() {
  const getRecaptchaToken = useRecaptchaToken();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setStatus({ state: "error", message: "Please enter your email address." });
      return;
    }
    setStatus({ state: "loading", message: "Sending reset link…" });
    try {
      const recaptcha_token = await getRecaptchaToken("investor-forgot-password");
      // Backend soft-fails on missing tokens — pass through as-is.
      const r = await Investor.forgotPassword({ email: email.trim(), recaptcha_token });
      setStatus({ state: "success", message: r?.message || "Check your inbox." });
    } catch (err) {
      setStatus({
        state: "error",
        message:
          err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          "Could not send the reset email. Please try again in a moment.",
      });
    }
  };

  return (
    <Shell testId="investor-forgot-password-page" eyebrow="Investor Portal · Reset" title="Forgot your password?">
      <p className="text-white/60 text-xs mt-1 leading-relaxed">
        Enter the email address registered on your Sahyog investor account. If it matches an
        account on file, we&apos;ll email you a secure link that expires in 15 minutes.
      </p>

      <form data-testid="investor-forgot-form" onSubmit={submit} className="mt-6">
        <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-2">
          Email address <span className="text-[#D4AF37]">*</span>
        </label>
        <div className="relative">
          <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="investor-forgot-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full pl-9 pr-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37] text-sm"
          />
        </div>

        {status.state === "error" && (
          <div
            data-testid="investor-forgot-error"
            className="mt-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm"
          >
            <AlertCircle size={14} className="mt-0.5 shrink-0" /> <span>{status.message}</span>
          </div>
        )}
        {status.state === "success" && (
          <div
            data-testid="investor-forgot-success"
            className="mt-4 flex items-start gap-2 px-3 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-100 text-sm leading-snug"
          >
            <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> <span>{status.message}</span>
          </div>
        )}

        <button
          data-testid="investor-forgot-submit"
          type="submit"
          disabled={status.state === "loading"}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold disabled:opacity-70"
        >
          <KeyRound size={16} />
          {status.state === "loading" ? "Sending…" : "Send reset link"}
        </button>

        <RecaptchaNotice />
      </form>
    </Shell>
  );
}

/* ─── STEP 2 · Reset / Activate password ─────────────────────────── */
export function InvestorResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const getRecaptchaToken = useRecaptchaToken();
  const [form, setForm] = useState({ pw: "", pw2: "" });
  const [status, setStatus] = useState({ state: token ? "idle" : "no-token", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (form.pw.length < 6) {
      setStatus({ state: "error", message: "Password must be at least 6 characters." });
      return;
    }
    if (form.pw !== form.pw2) {
      setStatus({ state: "error", message: "Passwords do not match." });
      return;
    }
    setStatus({ state: "loading", message: "Updating password…" });
    try {
      const recaptcha_token = await getRecaptchaToken("investor-reset-password");
      // Backend soft-fails on missing tokens — pass through as-is.
      await Investor.resetPassword({ token, new_password: form.pw, recaptcha_token });
      setStatus({ state: "success", message: "Password updated. Redirecting to sign-in…" });
      setTimeout(() => navigate("/investor/login", { replace: true }), 2000);
    } catch (err) {
      setStatus({
        state: "error",
        message:
          err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          "Could not reset the password. The link may have expired — please request a new one.",
      });
    }
  };

  if (status.state === "no-token") {
    return (
      <Shell testId="investor-reset-password-page" eyebrow="Investor Portal · Reset" title="Reset link missing">
        <p className="text-white/60 text-sm mt-3 leading-relaxed">
          The reset link is missing its security token. Please open the link exactly as it was
          emailed to you, or request a new one.
        </p>
        <Link
          to="/investor/forgot-password"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold"
        >
          Request a new link
        </Link>
      </Shell>
    );
  }

  return (
    <Shell testId="investor-reset-password-page" eyebrow="Investor Portal · Reset" title="Set your password">
      <p className="text-white/60 text-xs mt-1 leading-relaxed">
        Pick a strong password (min 6 characters). You&apos;ll sign in with your new password
        immediately after.
      </p>

      <form data-testid="investor-reset-form" onSubmit={submit} className="mt-6">
        <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-2">
          New password <span className="text-[#D4AF37]">*</span>
        </label>
        <input
          data-testid="investor-reset-new-pw"
          type="password"
          required
          minLength={6}
          value={form.pw}
          onChange={(e) => setForm((f) => ({ ...f, pw: e.target.value }))}
          placeholder="At least 6 characters"
          className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37] text-sm"
        />

        <label className="block text-[11px] uppercase tracking-wider text-white/55 mt-4 mb-2">
          Confirm new password <span className="text-[#D4AF37]">*</span>
        </label>
        <input
          data-testid="investor-reset-new-pw2"
          type="password"
          required
          minLength={6}
          value={form.pw2}
          onChange={(e) => setForm((f) => ({ ...f, pw2: e.target.value }))}
          placeholder="Type the same password again"
          className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37] text-sm"
        />

        {status.state === "error" && (
          <div
            data-testid="investor-reset-error"
            className="mt-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm"
          >
            <AlertCircle size={14} className="mt-0.5 shrink-0" /> <span>{status.message}</span>
          </div>
        )}
        {status.state === "success" && (
          <div
            data-testid="investor-reset-success"
            className="mt-4 flex items-start gap-2 px-3 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-100 text-sm"
          >
            <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> <span>{status.message}</span>
          </div>
        )}

        <button
          data-testid="investor-reset-submit"
          type="submit"
          disabled={status.state === "loading" || status.state === "success"}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold disabled:opacity-70"
        >
          <KeyRound size={16} />
          {status.state === "loading" ? "Updating…" : "Set password"}
        </button>

        <RecaptchaNotice />
      </form>
    </Shell>
  );
}
